/**
 * custom-cart-manager.js
 *
 * Central orchestrator for all cart mutations. Guarantees a single update
 * cycle per user action — no cascading events, no redundant fetches.
 *
 * Flow:
 *   Action → cart API → gift sync (inline) → dispatch custom:cart:updated
 *
 * Public API (window.CartManager):
 *   add(variantId, quantity?, properties?)  → Promise<void>
 *   change(line, quantity)                  → Promise<void>
 *   remove(key)                             → Promise<void>
 *   clear()                                 → Promise<void>
 *
 * Events dispatched:
 *   custom:cart:updated
 *     Primary event. detail: { cart, sections }
 *     Consumed by progressbar, upsell, and any custom component.
 *
 *   cart:update  (compatibility shim)
 *     detail: { _source: 'custom-cart-manager', data: { itemCount, sections } }
 *     Consumed by core cart-items-component for morphSection updates.
 *     Only dispatched when sections HTML is available or explicitly forced.
 *
 * Dependencies:
 *   - A <script type="application/json" id="cart-brindes-data"> element must
 *     be present in the DOM for gift sync to operate. Absent → sync skipped.
 */

window.CartManager = (() => {
  const EVT_UPDATED = 'custom:cart:updated';
  const EVT_CORE    = 'cart:update';
  const SOURCE      = 'custom-cart-manager';

  let _busy          = false;
  let _pendingCycle  = false;

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** Collect section IDs from all cart-items-component elements on the page. */
  function _getSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map(el => el.dataset.sectionId)
      .filter(Boolean);
  }

  /**
   * POST to a Shopify cart endpoint.
   * Automatically merges `sections` into the request body.
   * Throws on non-ok responses with a structured error.
   */
  async function _post(url, body) {
    const res  = await fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) {
      throw Object.assign(
        new Error(data?.description || data?.message || `Cart API ${res.status}`),
        { data }
      );
    }
    return data;
  }

  /** Fetch the full cart object from /cart.js */
  async function _fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  /**
   * Returns true if `data` is a full Shopify cart object
   * (i.e., from /cart/change.js or /cart/update.js — not /cart/add.js).
   */
  function _isFullCart(data) {
    return data != null && Array.isArray(data.items) && typeof data.total_price === 'number';
  }

  /** Read gift rules from the embedded JSON data block in the DOM. */
  function _getGiftRules() {
    try {
      const el = document.getElementById('cart-brindes-data');
      return el ? (JSON.parse(el.textContent).rules || []) : [];
    } catch {
      return [];
    }
  }

  // ── Toast ─────────────────────────────────────────────────────────────────

  function _toast(message, isError = false) {
    let el = document.getElementById('cart-manager-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cart-manager-toast';
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('role', 'status');
      Object.assign(el.style, {
        position:      'fixed',
        bottom:        '20px',
        right:         '20px',
        zIndex:        '9999',
        padding:       '12px 20px',
        borderRadius:  '8px',
        fontSize:      '14px',
        fontWeight:    '500',
        maxWidth:      '320px',
        boxShadow:     '0 4px 16px rgba(0,0,0,.18)',
        pointerEvents: 'none',
        opacity:       '0',
        transition:    'opacity .3s ease',
        background:    'var(--custom-clr-neutral-975)',
        color:         'var(--custom-clr-white)',
        borderLeft:    '4px solid var(--custom-clr-success-600)',
      });
      document.body.appendChild(el);
    }
    clearTimeout(el._timer);
    el.textContent           = message;
    el.style.borderLeftColor = isError ? 'var(--custom-clr-error-400)' : 'var(--custom-clr-success-600)';
    el.style.opacity         = '1';
    el._timer = setTimeout(() => { el.style.opacity = '0'; }, 4000);
  }

  // ── Gift sync ─────────────────────────────────────────────────────────────

  /**
   * Evaluates gift rules against the current cart.
   * Removes incorrect/priced gifts, adds the winning gift — all inline.
   * Returns the updated cart and any sections HTML from those mutations.
   * No events are dispatched here.
   *
   * @param {object} cart     - Full Shopify cart object.
   * @param {string[]} sectionIds - Section IDs for the sections param.
   * @returns {Promise<{ cart: object, sections: object }>}
   */
  async function _syncGifts(cart, sectionIds) {
    const mergedSections = {};
    let currentCart      = cart;

    // ── Phase 1: independent guard ──────────────────────────────────────────
    // Removes ANY item flagged as brinde (via properties._brinde) that has a
    // final_price > 0. This runs regardless of whether rules are configured,
    // catching stale or misconfigured gifts from previous sessions.
    const pricedGifts = cart.items.filter(
      item => item.properties?._brinde === 'true' && item.final_price > 0
    );

    for (const gift of pricedGifts) {
      console.warn('[CartManager] Removing priced brinde:', gift.key, gift.final_price);
      const res = await _post('/cart/change.js', {
        id:       gift.key,
        quantity: 0,
        sections: sectionIds,
      });
      if (res.sections)     Object.assign(mergedSections, res.sections);
      if (_isFullCart(res)) currentCart = res;
    }

    // Re-fetch after guard removals so threshold evaluation uses the real total
    if (pricedGifts.length) {
      currentCart = await _fetchCart();
    }

    // ── Phase 2: rules-based sync ───────────────────────────────────────────
    const rules = _getGiftRules();
    if (!rules.length) return { cart: currentCart, sections: mergedSections };

    const totalCents   = currentCart.total_price;
    const sorted       = [...rules].sort((a, b) => b.threshold - a.threshold);
    const winner       = sorted.find(r => r.variantId && totalCents >= r.threshold * 100) ?? null;
    const allVids      = new Set(rules.map(r => r.variantId).filter(Boolean));
    const giftsInCart  = currentCart.items.filter(item => allVids.has(item.variant_id));

    // Remove gifts that are: wrong variant, above threshold, or still priced
    const toRemove = giftsInCart.filter(item =>
      item.final_price > 0 ||
      !winner ||
      item.variant_id !== winner.variantId
    );
    const shouldAdd = winner && !giftsInCart.find(
      item => item.variant_id === winner.variantId && item.final_price === 0
    );

    if (!toRemove.length && !shouldAdd) return { cart: currentCart, sections: mergedSections };

    for (const gift of toRemove) {
      const res = await _post('/cart/change.js', {
        id:       gift.key,
        quantity: 0,
        sections: sectionIds,
      });
      if (res.sections)   Object.assign(mergedSections, res.sections);
      if (_isFullCart(res)) currentCart = res;
    }

    if (shouldAdd) {
      if (winner.available === false) {
        _toast(`Brinde indisponível: ${winner.title}`, true);
      } else {
        try {
          const res = await _post('/cart/add.js', {
            id:         winner.variantId,
            quantity:   1,
            properties: { _brinde: 'true' },
            sections:   sectionIds,
          });
          if (res.sections) Object.assign(mergedSections, res.sections);
          // /cart/add.js doesn't return a full cart — fetch fresh state
          currentCart = await _fetchCart();
          _toast(`🎁 ${winner.title} adicionado como brinde!`);
        } catch {
          _toast('Não foi possível adicionar o brinde.', true);
        }
      }
    } else if (toRemove.length && !winner) {
      _toast('Brinde removido do carrinho.');
    }

    return { cart: currentCart, sections: mergedSections };
  }

  // ── Emit ──────────────────────────────────────────────────────────────────

  /**
   * Dispatches the unified update event plus the core compatibility shim.
   *
   * The core cart:update shim is only dispatched when:
   *   - `sections` contains HTML (so cart-items-component can morph), OR
   *   - `forceCore` is true (for direct mutations like upsell add / clear)
   *
   * This avoids triggering a redundant sectionRenderer.renderSection call
   * after a core-originated update (product-form.js) where morph already ran.
   *
   * @param {object}  cart
   * @param {object}  sections
   * @param {object}  [opts]
   * @param {boolean} [opts.forceCore=false]
   */
  function _emit(cart, sections, { forceCore = false } = {}) {
    // Dispatch cart:update (core shim) FIRST so morphSection runs synchronously
    // and replaces the DOM before custom components hydrate it.
    // If custom:cart:updated fired first, the progressbar would update the DOM
    // and then morphSection would immediately reset it back to skeleton state.
    if (forceCore || Object.keys(sections).length > 0) {
      document.dispatchEvent(new CustomEvent(EVT_CORE, {
        bubbles: true,
        detail: {
          _source: SOURCE,
          data: {
            itemCount: cart.item_count,
            source:    SOURCE,
            sections,
          },
        },
      }));
    }

    // Dispatch custom:cart:updated AFTER morph so components hydrate the
    // already-updated DOM produced by morphSection above.
    document.dispatchEvent(new CustomEvent(EVT_UPDATED, {
      bubbles: true,
      detail:  { cart, sections },
    }));
  }

  // ── Cycle (reacting to core cart:update events) ───────────────────────────

  /**
   * Fetches the current cart, syncs gifts, then emits a single event.
   * Does NOT emit cart:update (shim) unless gifts changed, avoiding a
   * redundant re-render on top of the core's own morph.
   *
   * Uses _pendingCycle to queue exactly one extra run when called while
   * already busy — rapid events don't stack but none are lost.
   */
  async function _runCycle() {
    if (_busy) {
      _pendingCycle = true;
      return;
    }
    _busy         = true;
    _pendingCycle = false;
    try {
      const sectionIds                = _getSectionIds();
      const cart                      = await _fetchCart();
      const { cart: final, sections } = await _syncGifts(cart, sectionIds);

      // Safety-net: if the DOM still shows the empty state but the cart has
      // items (or vice-versa), the event-based morph from product-form.js
      // either didn't run or failed silently. Force a cart:update so that
      // cart-items-component falls back to sectionRenderer.renderSection and
      // fetches fresh section HTML.
      // #cart-drawer-heading-empty is only rendered in the {%- if cart.empty? -%} branch
      const uiShowsEmpty = !!document.getElementById('cart-drawer-heading-empty');
      const cartIsEmpty  = final.item_count === 0;
      const stateMismatch = uiShowsEmpty !== cartIsEmpty;

      _emit(final, sections, { forceCore: stateMismatch });
    } catch (e) {
      console.error('[CartManager] Cycle error:', e);
    } finally {
      _busy = false;
      if (_pendingCycle) {
        _pendingCycle = false;
        // Defer slightly to avoid re-entrancy; gift state is fresh so the
        // re-run will be a no-op unless the cart changed again.
        setTimeout(_runCycle, 0);
      }
    }
  }

  // ── Direct mutation (upsell, remove-all, etc.) ────────────────────────────

  /**
   * Executes a cart mutation, syncs gifts, then emits a single unified event.
   * Sections from the mutation response and gift sync are merged — the last
   * write (gift sync) wins, ensuring the final rendered HTML is up to date.
   *
   * @param {string} url   - Shopify cart endpoint.
   * @param {object} body  - Request body (sections injected automatically).
   */
  async function _mutate(url, body) {
    const sectionIds = _getSectionIds();
    const data       = await _post(url, { ...body, sections: sectionIds });

    const initialSections = data.sections || {};
    const cart            = _isFullCart(data) ? data : await _fetchCart();

    const { cart: final, sections: giftSections } = await _syncGifts(cart, sectionIds);

    // If gifts changed, use those sections (they reflect the final state).
    // Otherwise, use sections from the original mutation response.
    const finalSections = Object.keys(giftSections).length
      ? giftSections
      : initialSections;

    _emit(final, finalSections, { forceCore: true });
  }

  // ── Public API ────────────────────────────────────────────────────────────

  async function add(variantId, quantity = 1, properties = {}) {
    if (_busy) return;
    _busy = true;
    try     { await _mutate('/cart/add.js', { id: variantId, quantity, properties }); }
    catch   (e) { console.error('[CartManager] add error:', e); }
    finally { _busy = false; }
  }

  async function change(line, quantity) {
    if (_busy) return;
    _busy = true;
    try     { await _mutate('/cart/change.js', { line, quantity }); }
    catch   (e) { console.error('[CartManager] change error:', e); }
    finally { _busy = false; }
  }

  async function remove(key) {
    if (_busy) return;
    _busy = true;
    try     { await _mutate('/cart/change.js', { id: key, quantity: 0 }); }
    catch   (e) { console.error('[CartManager] remove error:', e); }
    finally { _busy = false; }
  }

  async function clear() {
    if (_busy) return;
    _busy = true;
    try {
      // /cart/clear.js returns the empty cart but no sections; let the
      // cart:update shim trigger sectionRenderer for the empty-cart view.
      const sectionIds = _getSectionIds();
      const data       = await _post('/cart/clear.js', { sections: sectionIds });
      const cart       = _isFullCart(data) ? data : await _fetchCart();
      const finalSections = data.sections || {};
      _emit(cart, finalSections, { forceCore: true });
    }
    catch   (e) { console.error('[CartManager] clear error:', e); }
    finally { _busy = false; }
  }

  // ── Listen to core events ─────────────────────────────────────────────────
  // Reacts to cart:update dispatched by product-form.js, cart-items-component, etc.
  // No debounce — _runCycle starts immediately so the gift sync begins as
  // soon as the first (product-add) morph has been applied synchronously.
  // Rapid events are coalesced via _pendingCycle.

  document.addEventListener(EVT_CORE, event => {
    if (event.detail?._source === SOURCE)  return; // our own shim
    if (event.detail?._brindeSource)       return; // legacy compat
    _runCycle();
  });

  // ── Initial check on page load ────────────────────────────────────────────
  // Replicates the old brinde guard's DOMContentLoaded behaviour:
  // catches any brinde item already in the cart with final_price > 0
  // (e.g. from a previous session where the discount wasn't applied).
  document.addEventListener('DOMContentLoaded', () => {
    _runCycle();
  });

  return { add, change, remove, clear };
})();
